<template>
  <div>
    <BaseSection :title="edit ? '' : data.name" :image="edit ? '' : data.logo">
      <div class="gap5" :class="edit ? 'd-flex flex-column' : 'grid'">
        <v-form ref="form" lazy-validation>
          <div class="gap5" :class="edit && $vuetify.breakpoint.lgAndUp ? 'grid__edit' : 'd-flex flex-column'">
            <div class="gap5" :class="edit ? 'grid__edit' : 'd-flex flex-column'">
              <div class="d-flex flex-column align-start gap5">
                <BaseCard v-if="edit" width="100%">
                  <BaseInput
                    :label="t['dialog-add_edit-labels']['nazvanie-kompanii']"
                    v-model="data.name"
                    :view="!edit"
                    :viewValue="data.name || '-'"
                    :rules="['required', 'max100']"
                  />
                  <div class="d-flex align-center gap5">
                    <v-avatar size="50" color="#f2f2f2">
                      <v-img v-if="data.logo" :src="apiDomain + '/min/' + data.logo" contain />
                    </v-avatar>
                    <UploadFile
                      :tooltip="t['dialog-add_edit-buttons'][data.logo ? 'izmenit-logotip' : 'zagruzit-logotip']"
                      :accept="['jpg', 'jpeg', 'png']"
                      :on-load="onLoadLogo"
                      :multiple="false"
                      :minimal="false"
                    />
                  </div>
                </BaseCard>
                <BaseCard width="100%">
                  <BaseInput
                    v-for="field in fields[0]"
                    :key="field.name"
                    :label="field.label"
                    v-model="data[field.name]"
                    :view="!edit"
                    :viewValue="data[field.name] || '-'"
                    :rules="field.rules"
                  />
                </BaseCard>
              </div>
              <div class="d-grid gap-5">
                <BaseCard :subtitle="t['dialog-add_edit']['elektronnyy-schet-faktura']">
                  <BaseInput
                    v-for="field in fields[1]"
                    :key="field.name"
                    :label="field.label"
                    v-model="data[field.name]"
                    :view="!edit"
                    :viewValue="data[field.name] || '-'"
                    :rules="field.rules"
                  />
                </BaseCard>
                <BaseCard v-if="companiesConfig && companiesConfig.fin_system === 'netvisor'">
                  <BaseInput
                    :label="t['tables']['netvisor_key']"
                    v-model="data['netvisor_id']"
                    :view="!edit"
                    :viewValue="data['netvisor_id'] || '-'"
                    input-type="Number"
                  />
                </BaseCard>
              </div>
            </div>
            <BaseCard>
              <div class="gap5" :class="edit ? 'grid__edit' : 'd-flex flex-column'">
                <template v-for="field in fields[2]">
                  <GoogleAutocomplete
                    v-if="edit"
                    :key="field.name + '_' + key"
                    :title="field.label"
                    :street="data[field.name + '_street']"
                    :zip="data[field.name + '_zip']"
                    :city="data[field.name + '_city']"
                    :external-data="{ field: field.name }"
                    :on-change="changeAddress"
                    class="flex-grow-1"
                    :required="false"
                  />
                  <BaseInput
                    v-else
                    :key="field.name"
                    :label="field.label"
                    :viewValue="(data[field.name + '_street'] ? data[field.name + '_street'] : '-') +
                      (data[field.name + '_zip'] ? ', ' + data[field.name + '_zip'] : '') +
                      (data[field.name + '_city'] ? ' ' + data[field.name + '_city'] : '')"
                    :rules="null"
                    view
                  />
                </template>
              </div>
            </BaseCard>
          </div>
        </v-form>
        <div v-if="!edit">
          <BasePagesWrapper :tab-list="tabList" />
          <WorkClientContact v-if="$route.query.tab === 'contacts'" />
          <WorkClientAddress v-if="$route.query.tab === 'address'" />
          <WorkClientPerson v-if="$route.query.tab === 'persons'" />
          <WorkClientOffices v-if="$route.query.tab === 'offices'" />
          <WorkClientProjects v-if="$route.query.tab === 'projects'" />
        </div>
      </div>
    </BaseSection>
    <div class="fixedBtn" v-if="$userCheckAccess({ abilities: 'edit_clients' })">
      <BaseTooltip v-if="edit" :tooltip="localStorage.translations.pages.common['buttons']['sohranit']">
        <BaseButton
          :disabled="!navigationGuardianActive"
          @click="onSave"
          round icon_only
          size="60"
          default_icon="button-save"
        />
      </BaseTooltip>
      <BaseTooltip v-else :tooltip="localStorage.translations.pages.common['buttons']['redaktirovat']">
        <BaseButton @click="onEdit" round size="60" icon_only default_icon="button-edit"/>
      </BaseTooltip>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import GoogleAutocomplete from 'components/google/autocomplete/GoogleAutocomplete'
import UploadFile from 'components/UploadFile'
import WorkClientContact from 'components/workClient/contact/WorkClientContact'
import WorkClientAddress from 'components/workClient/address/WorkClientAddress'
import WorkClientPerson from 'components/workClient/person/WorkClientPerson'
import WorkClientOffices from 'components/workClient/offices/WorkClientOffices'
import WorkClientProjects from 'components/workClient/WorkClientProjects.vue'

export default {
  name: 'clientsCompany',
  components: {
    WorkClientProjects,
    UploadFile,
    GoogleAutocomplete,
    WorkClientContact,
    WorkClientAddress,
    WorkClientPerson,
    WorkClientOffices,
  },
  data: () => ({
    key: 0,
  }),
  computed: {
    ...mapGetters(['apiDomain', 'companiesConfig']),
    ...mapGetters('workClient', { data: 'workClientShow' }),
    ...mapGetters('navigationGuardian', [
      'navigationGuardianActive',
      'navigationGuardianChangeData',
    ]),
    edit() {
      return this.$route.name.includes('Edit') || this.createPage
    },
    createPage() {
      return this.$route.name.includes('Create')
    },
    t() {
      return this.localStorage.translations.pages['setting-client']
    },
    fields() {
      return [
        [
          { label: this.t['tables']['vat'], name: 'vat', rules: ['max20'] },
          { label: this.t['tables']['y-tunnus'], name: 'ytunnus', rules: ['size9'] },
        ],
        [
          {
            label: this.t['tables']['adres-elektronnoy-nakladnoy-ovt'],
            name: 'ovt',
            rules: ['max20'],
          },
          { label: this.t['tables']['id-operatora'], name: 'operator_id', rules: ['max20'] },
          { label: this.t['tables']['operator'], name: 'operator', rules: ['max50'] },
        ],
        [
          { label: this.t['dialog-adress']['registratsionnyy-adres'], name: 'registration' },
          { label: this.t['dialog-adress']['adres-dlya-vystavleniya-schetov'], name: 'billing' },
        ],
      ]
    },
    tabList() {
      return [
        {
          abilities: 'view_clients',
          tab: 'offices',
          text: this.t['offices']['filialy'],
          showSubTab: false,
          subTab: [],
        },
        {
          abilities: 'view_client_addresses',
          tab: 'address',
          text: this.t['address']['adresa'],
          showSubTab: false,
          subTab: [],
        },
        {
          abilities: 'view_client_contacts',
          tab: 'contacts',
          text: this.t['contacts']['kontakty'],
          showSubTab: false,
          subTab: [],
        },
        {
          abilities: 'view_client_people',
          tab: 'persons',
          text: this.t['persons']['kontaktnye-litsa'],
          showSubTab: false,
          subTab: [],
        },
        {
          abilities: 'view_clients',
          tab: 'projects',
          text: this.localStorage.translations.pages.pages.buttons.proekty,
          showSubTab: true,
          subTab: [
            {
              abilities: 'view_clients',
              tab: 'current',
              text: this.localStorage.translations.pages.chat.all.online,
            },
            {
              abilities: 'view_clients',
              tab: 'sales',
              text: this.localStorage.translations.pages.pages.buttons.prodazhi,
            },
            {
              abilities: 'view_clients',
              tab: 'archive',
              text: this.localStorage.translations.pages.projects.pages.archive,
            },
          ]
        },
      ]
    },
  },
  watch: {
    data: {
      handler() {
        if (this.edit) this.onCheckChange({ overwrite: false })
      },
      deep: true,
    },
    '$route.name'() {
      this.onCheckChange({ overwrite: true })
    },
  },
  created() {
    if (!this.createPage) this.onLoadData()
  },
  beforeDestroy() {
    this.onWorkClientClear()
  },
  methods: {
    ...mapActions('workClient', [
      'onWorkClientShow',
      'onWorkClientUpdate',
      'onWorkClientRegistrationAddressUpdate',
      'onWorkClientBillingAddressUpdate',
      'onWorkClientCreate',
      'onWorkClientClear',
    ]),
    ...mapActions('navigationGuardian', ['onNavigationGuardianCheck']),
    async onLoadData() {
      await this.onWorkClientShow(this.$route.params.id).then(() => {
        if (this.edit) this.onCheckChange({ overwrite: true })
      })
    },
    onCheckChange({ overwrite }) {
      this.onNavigationGuardianCheck({
        data: {
          company: {
            id: this.data.id,
            name: this.data.name,
            logo: this.data.logo,
            vat: this.data.vat,
            ytunnus: this.data.ytunnus,
            ovt: this.data.ovt,
            operator_id: this.data.operator_id,
            operator: this.data.operator,
            netvisor_id: this.data.netvisor_id,
          },
          registration: {
            city: this.data.registration_city,
            zip: this.data.registration_zip,
            street: this.data.registration_street,
          },
          billing: {
            city: this.data.billing_city,
            zip: this.data.billing_zip,
            street: this.data.billing_street,
          },
        },
        overwrite,
      })
    },
    changeAddress(data) {
      let field = data.externalData.field
      this.data[field + '_street'] = data.street
      this.data[field + '_zip'] = data.zip
      this.data[field + '_city'] = data.city
      // this.key++
    },
    async onSave() {
      let error = false
      if (this.$refs.form.validate()) {
        if (this.data.id) {
          this.data.work_client_id = this.data.id
          if (this.navigationGuardianChangeData.company) await this.onWorkClientUpdate(this.data)
          if (this.navigationGuardianChangeData.registration)
            await this.onWorkClientRegistrationAddressUpdate(this.data)
          if (this.navigationGuardianChangeData.billing)
            await this.onWorkClientBillingAddressUpdate(this.data)
          if (!error) {
            this.onCheckChange({ overwrite: true })
            this.onEdit()
          }
        } else {
          await this.onWorkClientCreate(this.data).then(() => {
            this.onCheckChange({ overwrite: true })
            this.$router.push({
              name: this.$route.name.replace('Create', ''),
            })
          })
        }
      }
    },
    onEdit() {
      this.$router.push({
        name: this.edit ? this.$route.name.replace('Edit', '') : this.$route.name + 'Edit',
      })
    },
    onLoadLogo(data) {
      this.data.logo = data[0].url
    },
  },
}
</script>

<style scoped lang="scss">
.grid {
  display: grid;
  grid-template-columns: minmax(280px, 25%) auto;
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  &__edit {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: start;
    @media #{map-get($display-breakpoints, 'xs-only')} {
      grid-template-columns: 1fr;
    }
  }
}
</style>